if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        // FIX: Support both name="id" (no add-ons) and name="id[]" (with add-ons).
        // Previously querySelector('[name=id]') threw when the input used name="id[]".
        const mainIdInput =
          this.form.querySelector('[name=id]') ||
          this.form.querySelector('[name="id[]"]');
        if (mainIdInput) mainIdInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // ── Detect add-ons ──────────────────────────────────────────────────
        // Collect every checked id[] checkbox that carries a variant ID.
        // These are the hidden add-on inputs rendered by buy-buttons.liquid.
        const addonIds = Array.from(
          this.form.querySelectorAll('input[name="id[]"]:checked')
        )
          .map((el) => el.value)
          .filter(Boolean);

        const isMultiItem = addonIds.length > 0;

        if (isMultiItem) {
          // ── Multi-item path ───────────────────────────────────────────────
          // Shopify's /cart/add endpoint accepts an `items` JSON array.
          // FormData with name="id[]" is NOT supported and returns "Bad Request",
          // which caused: SyntaxError: Unexpected token 'B', "Bad Request"...
          const mainId =
            this.form.querySelector('[name=id]')?.value ||
            this.form.querySelector('[name="id[]"]')?.value;

          const items = [
            { id: parseInt(mainId, 10), quantity: 1 },
            ...addonIds.map((id) => ({ id: parseInt(id, 10), quantity: 1 })),
          ];

          // Include sections payload for cart drawer / notification
          const sectionsPayload = {};
          if (this.cart) {
            sectionsPayload.sections = this.cart
              .getSectionsToRender()
              .map((s) => s.id);
            sectionsPayload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items, ...sectionsPayload }),
          })
            .then((res) => {
              // Guard: if Shopify returns non-JSON (e.g. a 400 plain-text body),
              // surface a readable error instead of crashing on .json().
              const contentType = res.headers.get('content-type') || '';
              if (!contentType.includes('application/json')) {
                return res.text().then((text) => {
                  throw new Error(`Cart add failed (${res.status}): ${text}`);
                });
              }
              return res.json();
            })
            .then((response) => {
              this._handleCartResponse(response, items[0].id);
            })
            .catch((e) => {
              console.error('Multi-item cart add error:', e);
              this.handleErrorMessage(
                e.message || 'Something went wrong. Please try again.'
              );
            })
            .finally(() => this._resetSubmitButton());

        } else {
          // ── Single-item path (original, unchanged) ────────────────────────
          // Products without any checked add-ons continue to use the original
          // FormData approach so nothing changes for the standard cart flow.
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((s) => s.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              this._handleCartResponse(response, formData.get('id'));
            })
            .catch((e) => {
              console.error('Single-item cart add error:', e);
            })
            .finally(() => this._resetSubmitButton());
        }
      }

      // ── Shared response handler ─────────────────────────────────────────────
      _handleCartResponse(response, variantId) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage =
            this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.querySelector('span').classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        }

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: response,
          });
        }
        this.error = false;

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => { setTimeout(() => this.cart.renderContents(response)); },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          this.cart.renderContents(response);
        }
      }

      // ── Re-enable submit button after fetch completes ───────────────────────
      _resetSubmitButton() {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty'))
          this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}
